// PersonMenu — the signed-in person's menu in the top bar, as the EquaOS frame
// has it since 1.6.0 (issue 24, Sanjoy 29 Oct 2026): who you are, "Sign-in
// and security", and Sign out right below it, where people look for it.
//
// Hiring is not on the frame yet (it is not a Vite app; moving it is a later
// job), so this is the frame's behaviour copied by hand. Sign-in and security
// is the school's Authentication app (auth.<school address>/me/security), the
// page the frame opens; away from a school's address there is none, and the
// menu offers Sign out only. Never past the bottom of the window.

import React, { useEffect, useRef, useState } from 'react';
import { Button, Icon } from '../../ds';

/* The school's Authentication app: https://auth.<school>/me/security. */
export function securityAddress(schoolAddress) {
  if (!schoolAddress) return null;
  try {
    const u = new URL(schoolAddress);
    return `${u.protocol}//auth.${u.host}/me/security`;
  } catch { return null; }
}

export default function PersonMenu({ person, schoolAddress, onSignOut }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const security = securityAddress(schoolAddress);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', esc); };
  }, [open]);

  const item = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', textAlign: 'left',
    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-1)', fontSize: 'var(--fs-body-sm)', textDecoration: 'none',
  };

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <Button variant="ghost" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={`${person.name}: account menu`}>
        <span style={{
          width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 600,
          background: 'var(--brand-primary-soft)', color: 'var(--brand-primary-ink)',
        }}>{person.initials}</span>
      </Button>
      {open && (
        <div role="menu" aria-label="Account" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)', minWidth: 240, zIndex: 60,
          maxHeight: 'calc(100vh - 88px)', overflowY: 'auto', overscrollBehavior: 'contain',
          background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-overlay)', padding: '6px 0',
        }}>
          <div style={{ padding: '10px 16px 12px', borderBottom: '1px solid var(--border)', marginBottom: 6 }}>
            <div style={{ fontWeight: 600 }}>{person.name}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>{person.role}</div>
          </div>
          {security && (
            <a role="menuitem" href={security} style={item} onClick={() => setOpen(false)}>
              <Icon name="shield-check" />Sign-in and security
            </a>
          )}
          {/* Right below sign-in, where people look for it (issue 24). */}
          <button type="button" role="menuitem" style={item} onClick={() => { setOpen(false); onSignOut(); }}>
            <Icon name="sign-out" />Sign out
          </button>
        </div>
      )}
    </div>
  );
}
